import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Search, X, ListMusic } from "lucide-react";
import api from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

function thumb(id) {
  return `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
}

const CATEGORIES = ["All", "Worship", "Afro Gospel"];

export default function Watch() {
  const [messages, setMessages] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("All");
  const [series, setSeries] = useState("All");
  const [query, setQuery] = useState("");
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    api.get("/messages").then(({ data }) => setMessages(data)).catch(() => {});
  }, []);

  const heroList = messages.filter((m) => m.featured);
  const rotation = heroList.length ? heroList : messages.slice(0, 1);

  // Auto-rotate the featured hero
  useEffect(() => {
    if (rotation.length <= 1) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % rotation.length), 6000);
    return () => clearInterval(t);
  }, [rotation.length]);

  const hero = rotation[heroIdx % (rotation.length || 1)];

  // Series list — derived from data
  const seriesList = useMemo(() => {
    const map = new Map();
    messages.forEach((m) => {
      if (!m.series) return;
      if (!map.has(m.series)) map.set(m.series, []);
      map.get(m.series).push(m);
    });
    return Array.from(map.entries()).map(([name, items]) => ({ name, items }));
  }, [messages]);

  const seriesOptions = ["All", ...seriesList.map((s) => s.name)];

  const isSearching = query.trim().length > 0;
  const q = query.trim().toLowerCase();

  const gridMessages = messages.filter((m) => {
    // exclude hero only when nothing else is filtering
    if (!isSearching && filter === "All" && series === "All" && hero && m.id === hero.id) return false;
    if (filter !== "All" && m.category !== filter) return false;
    if (series !== "All" && m.series !== series) return false;
    if (isSearching) {
      const hay = `${m.title} ${m.speaker} ${m.category} ${m.series || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const clearAll = () => { setQuery(""); setFilter("All"); setSeries("All"); };

  return (
    <div data-testid="watch-page">
      <PageHero
        overline="Watch Messages"
        title={<>Worship & teaching that <span className="font-accent text-gold-gradient italic">transforms.</span></>}
        subtitle="Encounter God through Spirit-led worship, sound biblical teaching, and messages that build Kingdom leaders."
      />

      {/* Featured (auto-rotating) — hidden while user is actively searching */}
      {hero && !isSearching && (
        <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-20">
          <Reveal>
            <div className="relative overflow-hidden border border-gold-500/25 corner-frame" data-testid="featured-video">
              <AnimatePresence mode="wait">
                <motion.button
                  key={hero.id}
                  onClick={() => setActive(hero)}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className="group relative block w-full"
                  data-testid="featured-play"
                >
                  <img src={thumb(hero.id)} onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${hero.id}/hqdefault.jpg`; }} alt={hero.title} className="aspect-video w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-20 w-20 items-center justify-center rounded-full bg-gold-500 text-ink transition-transform duration-500 group-hover:scale-110">
                      <Play size={30} className="ml-1.5" fill="currentColor" />
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 p-8 md:p-12 text-left">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-gold-400">Featured · {hero.category}</span>
                    <h2 className="mt-3 font-serif text-3xl md:text-5xl text-white">{hero.title}</h2>
                    <p className="mt-2 text-sm text-white/60">{hero.speaker}</p>
                  </div>
                </motion.button>
              </AnimatePresence>
              {/* rotation dots */}
              {rotation.length > 1 && (
                <div className="absolute bottom-6 right-6 md:bottom-10 md:right-12 flex gap-2 z-10" data-testid="hero-dots">
                  {rotation.map((m, i) => (
                    <button
                      key={m.id}
                      onClick={() => setHeroIdx(i)}
                      aria-label={`Show ${m.title}`}
                      data-testid={`hero-dot-${i}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${i === heroIdx % rotation.length ? "w-8 bg-gold-500" : "w-3 bg-white/40 hover:bg-white/70"}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        </section>
      )}

      {/* Series & Playlists */}
      {seriesList.length > 0 && !isSearching && series === "All" && (
        <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-8 md:pb-14">
          <Reveal className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ListMusic size={18} className="text-gold-400" />
              <Overline>Series & Playlists</Overline>
            </div>
            <span className="text-xs text-white/40 tracking-[0.15em] uppercase hidden md:block">Binge a theme</span>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="series-grid">
            {seriesList.map((s, i) => (
              <Reveal key={s.name} delay={(i % 4) * 0.05}>
                <button
                  onClick={() => setSeries(s.name)}
                  data-testid={`series-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group flex h-full w-full flex-col text-left"
                >
                  <div className="relative overflow-hidden border border-gold-500/20 aspect-[4/3]">
                    <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-[2px]">
                      {s.items.slice(0, 4).map((it, idx) => (
                        <img
                          key={it.id + idx}
                          src={thumb(it.id)}
                          onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${it.id}/hqdefault.jpg`; }}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ))}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                    <span className="absolute left-3 top-3 bg-ink/80 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gold-400 border border-gold-500/30">
                      Playlist · {s.items.length}
                    </span>
                  </div>
                  <h3 className="mt-4 font-serif text-xl text-white group-hover:text-gold-300 transition-colors">{s.name}</h3>
                  <p className="mt-1 text-sm text-white/55">
                    {s.items[0]?.speaker}
                    {s.items.length > 1 ? ` · +${s.items.length - 1} more` : ""}
                  </p>
                </button>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Search + Filter + Grid */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-28">
        <Reveal className="mb-10 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <Overline>
              {series !== "All" ? `Playlist · ${series}` : isSearching ? "Search Results" : "More Messages"}
            </Overline>
            <div className="flex flex-wrap gap-2" data-testid="watch-filters">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`watch-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                    filter === c ? "border-gold-400 text-gold-300 bg-gold-500/10" : "border-white/15 text-white/60 hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Search input + Series pills */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-md" data-testid="watch-search-wrapper">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title or speaker…"
                data-testid="watch-search-input"
                className="w-full bg-transparent border border-white/15 pl-11 pr-11 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-gold-400 transition-colors"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  data-testid="watch-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-gold-300"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {seriesOptions.length > 1 && (
              <div className="flex flex-wrap gap-2" data-testid="series-filters">
                {seriesOptions.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSeries(s)}
                    data-testid={`series-filter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`px-3 py-2 text-[11px] uppercase tracking-[0.15em] border transition-colors duration-300 ${
                      series === s ? "border-gold-400 text-gold-300 bg-gold-500/10" : "border-white/10 text-white/50 hover:text-white"
                    }`}
                  >
                    {s === "All" ? "All Series" : s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {(isSearching || filter !== "All" || series !== "All") && (
            <div className="flex items-center justify-between text-xs text-white/50">
              <span data-testid="watch-results-count">
                {gridMessages.length} {gridMessages.length === 1 ? "result" : "results"}
              </span>
              <button
                onClick={clearAll}
                data-testid="watch-clear-all"
                className="uppercase tracking-[0.2em] text-gold-400 hover:text-gold-300 transition-colors"
              >
                Clear filters
              </button>
            </div>
          )}
        </Reveal>

        {gridMessages.length === 0 ? (
          <p data-testid="watch-empty" className="text-white/50 py-10 text-center border border-gold-500/15">
            {isSearching ? `No messages match “${query}”.` : "No messages in this category yet."}
          </p>
        ) : (
          <motion.div layout className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="watch-grid">
            {gridMessages.map((m, i) => (
              <Reveal key={m.id} delay={(i % 3) * 0.05}>
                <button
                  onClick={() => setActive(m)}
                  data-testid={`video-${m.id}`}
                  className="group flex h-full w-full flex-col text-left"
                >
                  <div className="relative overflow-hidden border border-gold-500/20 aspect-video">
                    <img src={thumb(m.id)} onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${m.id}/hqdefault.jpg`; }} alt={m.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-ink/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gold-500 text-ink">
                        <Play size={20} className="ml-1" fill="currentColor" />
                      </span>
                    </div>
                    <span className="absolute left-3 top-3 bg-ink/80 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gold-400 border border-gold-500/30">{m.category}</span>
                    {m.series && (
                      <span className="absolute right-3 top-3 bg-ink/80 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-white/70 border border-white/20">
                        {m.series}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-4 font-serif text-xl text-white group-hover:text-gold-300 transition-colors">{m.title}</h3>
                  <p className="mt-1 text-sm text-white/55">{m.speaker}</p>
                </button>
              </Reveal>
            ))}
          </motion.div>
        )}
      </section>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-ink border-gold-500/25 text-white max-w-4xl p-2 sm:p-3" data-testid="video-modal">
          <DialogTitle className="sr-only">{active?.title}</DialogTitle>
          <DialogDescription className="sr-only">Video player for {active?.title}</DialogDescription>
          {active && (
            <div className="aspect-video w-full">
              <iframe
                className="h-full w-full"
                src={`https://www.youtube.com/embed/${active.id}?autoplay=1&rel=0`}
                title={active.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
